import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Modal from "react-modal";
import SVG from "react-inlinesvg";
import copy from "copy-to-clipboard";

import { PageLayout } from "../../page-layout";
import { PageLoader } from "../../page-loader";
import copyIcon from "../../../images/icons/copy.svg";
import successIcon from "../../../images/icons/success.svg";
import apiKeyIcon from "../../../images/icons/api-key.svg";
import {
  isReauthInProgressError,
  withTokenRecovery,
} from "../../../utils/tokenUtils";

const customStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
  },
};

const Auth0Keys = () => {
  const {
    isLoading: auth0IsLoading,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();
  const [APIKey, setAPIKey] = useState("");
  const [modalIsOpen, setIsOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  let isLoading = auth0IsLoading;

  Modal.setAppElement("#root");

  async function createKey() {
    setLoading(true);

    try {
      const authApiCall = withTokenRecovery({
        getAccessTokenSilently,
        loginWithRedirect,
        audience: process.env.REACT_APP_DEV_PORTAL_API_SERVER,
        context: "keys",
        returnTo: "/keys",
      });

      const result = await authApiCall(async (token) => {
        const response = await fetch(
          `${process.env.REACT_APP_DEV_PORTAL_API_SERVER}/create-key`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const error = new Error(`Failed to create key: ${response.status}`);
          error.status = response.status;
          throw error;
        }

        return response.json();
      });

      console.log("API key created successfully:", result);
      setAPIKey(result.apikey);
      openModal();
    } catch (error) {
      if (isReauthInProgressError(error)) return;
      console.error("Error creating key:", error);
      const errorMessage =
        error.message || "Unknown error occurred while creating API key";
      setAPIKey(`Error creating key: ${errorMessage}`);
      openModal();
    } finally {
      setLoading(false);
    }
  }

  function openModal() {
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  if (isLoading) {
    return <PageLoader />;
  }

  return (
    <PageLayout>
      <div className="keys-description">
        <h1>Keys</h1>
        <h2>
          On this page, you can create an API key to access{"\n"}the APIs that
          are protected through key-auth.
        </h2>
        <div>
          <p>
            To use the API key, add an <code>x-api-key</code> header to your API
            request with the{"\n"}generated key as the value.
            {"\n\n"}
            <strong>Note: </strong>
            Make sure to store the key somewhere safe as you will not be{"\n"}
            able to retrieve it once you close the modal.
          </p>
        </div>

        <div className="page-layout__focus">
          <div className="loader-key-wrapper md:h-[40px]">
            {loading && <PageLoader />}
          </div>
          <button
            className={
              "button__purp disabled:bg-opacity-50 disabled:cursor-not-allowed"
            }
            disabled={loading}
            onClick={createKey}
          >
            {loading ? "Creating..." : "Create Key"}
          </button>
        </div>
        <p>
          <i>*API may not always return accurate or factually correct answers.</i>
        </p>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel="API Key"
      >
        <h3 className="modal-title">Copy API Key</h3>
        <div className="modal-body">
          <label>Your API Key</label>
          <div className="api-key-container">
            <span className="api-key-presentation">
              <SVG src={apiKeyIcon} />
              <pre className="api-key">{APIKey}</pre>
            </span>
            <button
              className="copy-button"
              onClick={() => {
                const successfulCopy = copy(APIKey);
                if (successfulCopy) {
                  setIsCopied(true);
                  setTimeout(() => setIsCopied(false), 3000);
                }
              }}
            >
              <SVG
                className="icon"
                style={{ width: "15px", height: "13.5px" }}
                fill="currentcolor"
                src={isCopied ? successIcon : copyIcon}
              />
            </button>
          </div>
        </div>
        <div className="modal-footer">
          <button
            className="button button--outline-secondary"
            onClick={closeModal}
          >
            Close
          </button>
        </div>
      </Modal>
    </PageLayout>
  );
};

export default Auth0Keys;
